package com.daytimegaming.ourdailyfinances.presentation.budget

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.Budget
import com.daytimegaming.ourdailyfinances.domain.model.Dashboard
import com.daytimegaming.ourdailyfinances.domain.model.DashboardDetail
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.BudgetUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.DashboardUseCase
import com.daytimegaming.ourdailyfinances.presentation.home.BudgetProgress
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.number
import kotlinx.datetime.todayIn
import kotlin.time.Clock

sealed interface ViewBudgetScreenState {
    data object Loading : ViewBudgetScreenState
    data class Error(val message: String) : ViewBudgetScreenState
    data class Loaded(
        val budget: Budget,
        val progress: BudgetProgress,
        val dashboards: List<Dashboard>,
        val transactions: List<Transaction>,
        val currentMonthDaily: List<Double>,
        val previousMonthDaily: List<Double>,
        val currentMonthCumulative: List<Double>,
        val previousMonthCumulative: List<Double>,
        val totalCurrent: Double,
        val totalPrev: Double,
        val totalPrevUpToSameDay: Double,
        val currentMonthName: String,
        val previousMonthName: String,
        val accountNames: Map<String, String> = emptyMap(),
        val institutionNames: Map<String, String?> = emptyMap(),
    ) : ViewBudgetScreenState
}

@OptIn(ExperimentalCoroutinesApi::class)
class ViewBudgetViewModel(
    private val budgetId: String,
    private val budgetUseCase: BudgetUseCase,
    private val dashboardUseCase: DashboardUseCase,
    private val transactionRepository: TransactionRepository,
    private val accountUseCase: AccountUseCase
) : ViewModel() {

    private val _state = MutableStateFlow<ViewBudgetScreenState>(ViewBudgetScreenState.Loading)
    val state = _state.asStateFlow()

    init {
        load()
    }

    fun load() {
        viewModelScope.launch {
            transactionRepository.refresh()
        }
        viewModelScope.launch {
            _state.update { ViewBudgetScreenState.Loading }

            val budgetsFlow = budgetUseCase.GetBudgets()
            val dashboardsFlow = dashboardUseCase.GetDashboards()
            val transactionsFlow = transactionRepository.transactions
            val accountsFlow = accountUseCase.GetAccounts()
            val plaidItemsFlow = accountUseCase.GetPlaidItems()

            budgetsFlow.flatMapLatest { budgetsResponse ->
                when (budgetsResponse) {
                    is Response.Error -> flowOf(ViewBudgetScreenState.Error(budgetsResponse.message))
                    Response.Loading -> flowOf(ViewBudgetScreenState.Loading)
                    is Response.Success -> {
                        val budget = budgetsResponse.data.find { it.budgetId == budgetId }
                        if (budget == null) {
                            flowOf(ViewBudgetScreenState.Error("Budget not found"))
                        } else {
                            val dashboardDetailFlows = if (budget.dashboardIds.isNotEmpty()) {
                                val detailFlows = budget.dashboardIds.map { dId ->
                                    dashboardUseCase.GetDashboardDetail(dId)
                                }
                                combine(detailFlows) { responses ->
                                    val error = responses.filterIsInstance<Response.Error>().firstOrNull()
                                    if (error != null) {
                                        Response.Error(error.message)
                                    } else if (responses.any { it is Response.Loading }) {
                                        Response.Loading
                                    } else {
                                        val details = responses.filterIsInstance<Response.Success<DashboardDetail>>().map { it.data }
                                        Response.Success(details)
                                    }
                                }
                            } else {
                                flowOf(Response.Success(emptyList<DashboardDetail>()))
                            }

                            combine(
                                dashboardsFlow,
                                transactionsFlow,
                                accountsFlow,
                                plaidItemsFlow,
                                dashboardDetailFlows
                            ) { dashboardsResponse, transactionsResponse, accountsResponse, plaidItemsResponse, detailsResponse ->
                                when {
                                    dashboardsResponse is Response.Error -> ViewBudgetScreenState.Error(dashboardsResponse.message)
                                    transactionsResponse is Response.Error -> ViewBudgetScreenState.Error(transactionsResponse.message)
                                    accountsResponse is Response.Error -> ViewBudgetScreenState.Error(accountsResponse.message)
                                    plaidItemsResponse is Response.Error -> ViewBudgetScreenState.Error(plaidItemsResponse.message)
                                    detailsResponse is Response.Error -> ViewBudgetScreenState.Error(detailsResponse.message)
                                    dashboardsResponse is Response.Loading || transactionsResponse is Response.Loading || accountsResponse is Response.Loading || plaidItemsResponse is Response.Loading || detailsResponse is Response.Loading -> {
                                        ViewBudgetScreenState.Loading
                                    }
                                    dashboardsResponse is Response.Success && transactionsResponse is Response.Success && accountsResponse is Response.Success && plaidItemsResponse is Response.Success && detailsResponse is Response.Success -> {
                                        val dashboards = dashboardsResponse.data.filter { it.dashboardId in budget.dashboardIds }
                                        val relevantTransactions = if (budget.dashboardIds.isNotEmpty()) {
                                            detailsResponse.data
                                                .flatMap { it.transactions }
                                                .distinctBy { it.transactionId }
                                        } else {
                                            transactionsResponse.data
                                        }

                                        val budgetTx = relevantTransactions.filter { tx ->
                                            tx.amount > 0.0 && tx.activeCategory?.detailed in budget.categories
                                        }

                                        val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
                                        val currentMonth = today.month.number
                                        val currentYear = today.year
                                        val previousMonthDate = today.minus(1, DateTimeUnit.MONTH)
                                        val prevMonth = previousMonthDate.month.number
                                        val prevYear = previousMonthDate.year

                                        val daysInCurrentMonth = getDaysInMonth(currentMonth, currentYear)
                                        val daysInPrevMonth = getDaysInMonth(prevMonth, prevYear)

                                        val currentMonthDaily = MutableList(daysInCurrentMonth) { 0.0 }
                                        val previousMonthDaily = MutableList(daysInPrevMonth) { 0.0 }
                                        val currentMonthTransactions = mutableListOf<Transaction>()

                                        budgetTx.forEach { tx ->
                                            try {
                                                val date = LocalDate.parse(tx.date)
                                                if (date.year == currentYear && date.month.number == currentMonth) {
                                                    currentMonthTransactions.add(tx)
                                                    val dayIndex = date.day - 1
                                                    if (dayIndex in 0 until daysInCurrentMonth) {
                                                        currentMonthDaily[dayIndex] += tx.amount
                                                    }
                                                } else if (date.year == prevYear && date.month.number == prevMonth) {
                                                    val dayIndex = date.day - 1
                                                    if (dayIndex in 0 until daysInPrevMonth) {
                                                        previousMonthDaily[dayIndex] += tx.amount
                                                    }
                                                }
                                            } catch (e: Exception) {}
                                        }

                                        val sortedCurrentTransactions = currentMonthTransactions.sortedByDescending { it.date }

                                        var currentSum = 0.0
                                        val currentMonthCumulative = currentMonthDaily.map {
                                            currentSum += it
                                            currentSum
                                        }

                                        var prevSum = 0.0
                                        val previousMonthCumulative = previousMonthDaily.map {
                                            prevSum += it
                                            prevSum
                                        }

                                        val totalCurrent = currentMonthDaily.sum()
                                        val totalPrev = previousMonthDaily.sum()
                                        val totalPrevUpToSameDay = previousMonthDaily.take(today.day).sum()

                                        val progress = if (budget.targetValue > 0.0) {
                                            (totalCurrent / budget.targetValue).toFloat()
                                        } else {
                                            0.0f
                                        }

                                        val changePercentage = if (totalPrev > 0.0) {
                                            ((totalCurrent - totalPrev) / totalPrev) * 100.0
                                        } else {
                                            null
                                        }

                                        val budgetProgress = BudgetProgress(
                                            currentMonthSpending = totalCurrent,
                                            previousMonthSpending = totalPrev,
                                            changePercentage = changePercentage,
                                            progress = progress
                                        )

                                        val currentMonthName = today.month.name.lowercase().replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
                                        val previousMonthName = previousMonthDate.month.name.lowercase().replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }

                                        val accountNames = mutableMapOf<String, String>()
                                        val institutionNames = mutableMapOf<String, String?>()

                                        val plaidItems = plaidItemsResponse.data
                                        accountsResponse.data.forEach { acc ->
                                            accountNames[acc.accountId] = acc.name
                                            val institutionName = plaidItems.find { it.itemId == acc.itemId }?.institutionName
                                            institutionNames[acc.accountId] = institutionName
                                        }

                                        ViewBudgetScreenState.Loaded(
                                            budget = budget,
                                            progress = budgetProgress,
                                            dashboards = dashboards,
                                            transactions = sortedCurrentTransactions,
                                            currentMonthDaily = currentMonthDaily,
                                            previousMonthDaily = previousMonthDaily,
                                            currentMonthCumulative = currentMonthCumulative,
                                            previousMonthCumulative = previousMonthCumulative,
                                            totalCurrent = totalCurrent,
                                            totalPrev = totalPrev,
                                            totalPrevUpToSameDay = totalPrevUpToSameDay,
                                            currentMonthName = currentMonthName,
                                            previousMonthName = previousMonthName,
                                            accountNames = accountNames,
                                            institutionNames = institutionNames
                                        )
                                    }
                                    else -> ViewBudgetScreenState.Loading
                                }
                            }
                        }
                    }
                }
            }.collect { newState ->
                _state.update { newState }
            }
        }
    }

    private fun getDaysInMonth(month: Int, year: Int): Int {
        return when (month) {
            2 -> if ((year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)) 29 else 28
            4, 6, 9, 11 -> 30
            else -> 31
        }
    }
}
